import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import {
  ColumnDef,
  RowSelectionState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Checkbox } from 'components/ui/checkbox';
import { DataTable } from 'components/ui/data-table';
import { Input } from 'components/ui/input';
import { DatasetColumn } from 'interfaces/column';
import { IndicatorVariable } from 'interfaces/indicator';
import React, { useEffect, useMemo, useState } from 'react';
import { getCharRange } from 'utils/utils';

type DatasetVariablePickerProps = {
  columns: DatasetColumn[];
  onChange: (variables: IndicatorVariable[]) => void;
};

const ALPHABET_RANGE = getCharRange('A', 'Z');

const DatasetVariablePicker = React.forwardRef<
  HTMLDivElement,
  DatasetVariablePickerProps
>(({ columns, onChange }, ref) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchKeywords, setSearchKeywords] = useState<string>('');

  const filteredRows = useMemo(() => {
    return searchKeywords
      ? columns.filter((row) => {
          return row.name.includes(searchKeywords);
        })
      : columns;
  }, [columns, searchKeywords]);

  const selectedColumnNames = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection]
  );

  useEffect(() => {
    const selected = selectedColumnNames.map((name, idx) => {
      const selectedColumn = columns.find((column) => column.name === name);
      return {
        columnName: name,
        alias: ALPHABET_RANGE[idx],
        sample: selectedColumn ? selectedColumn.sample : 1,
      };
    });
    onChange(selected);
  }, [columns, onChange, selectedColumnNames]);

  const tableColumns = useMemo<ColumnDef<DatasetColumn>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Sélectionner toutes les colonnes"
            className="mx-auto"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Sélectionner ${row.original.name}`}
            className="mx-auto"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        meta: {
          className: 'w-12 text-center',
          cellClassName: 'text-center',
        },
      },
      {
        accessorKey: 'name',
        header: 'Colonne',
      },
      {
        accessorKey: 'sample',
        header: 'Échantillon',
      },
      {
        accessorKey: 'source',
        header: 'Source',
      },
      {
        accessorKey: 'validity',
        header: 'Validité',
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredRows,
    columns: tableColumns,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.name,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="border-slate-300" ref={ref}>
      <div className="relative mb-2">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <Input
          id="keywords"
          className="w-full pl-9"
          value={searchKeywords}
          onChange={(e) => setSearchKeywords(e.target.value)}
          autoComplete={'false'}
        />
      </div>
      <DataTable table={table} className="max-h-96 overflow-y-auto" />
    </div>
  );
});

DatasetVariablePicker.displayName = 'DatasetVariablePicker';

export default DatasetVariablePicker;
